/**
 * Cognito Authorization Code + PKCE helpers (§30.3, §32.1, ADR 0003).
 *
 * Uses the shared "ddo" hosted-UI pool. All values are overridable via Vite env
 * (`import.meta.env.VITE_COGNITO_*`) with the discovered defaults from the ADR. This module is
 * pure crypto/URL logic and holds no tokens — storage lives in {@link AuthContext} (in memory only).
 */

export interface CognitoConfig {
  readonly domain: string;
  readonly region: string;
  readonly clientId: string;
  readonly scopes: string;
}

export interface TokenSet {
  readonly accessToken: string;
  readonly idToken: string;
  /** Refresh token is absent on a refresh_token grant response — Cognito reuses the existing one. */
  readonly refreshToken: string | null;
  readonly tokenType: string;
  /** Absolute expiry, epoch milliseconds, computed from `expires_in`. */
  readonly expiresAt: number;
}

interface CognitoTokenResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  token_type: string;
  expires_in: number;
}

function readEnv(key: string): string | undefined {
  // `import.meta.env` is inlined by Vite/vitest; guard so tooling that lacks it does not throw.
  const meta = import.meta as unknown as { env?: Record<string, string | undefined> };
  return meta.env?.[key];
}

/** Resolve Cognito client configuration from Vite env with ADR-0003 defaults. */
export function resolveCognitoConfig(): CognitoConfig {
  return {
    domain: readEnv('VITE_COGNITO_DOMAIN') ?? 'ddo-auth',
    region: readEnv('VITE_COGNITO_REGION') ?? 'us-east-1',
    clientId: readEnv('VITE_COGNITO_CLIENT_ID') ?? '187rdft83t39r8mu0l1j644nll',
    scopes: readEnv('VITE_COGNITO_SCOPES') ?? 'openid email profile',
  };
}

/** Hosted-UI origin, e.g. https://ddo-auth.auth.us-east-1.amazoncognito.com. */
export function hostedUiOrigin(config: CognitoConfig = resolveCognitoConfig()): string {
  return `https://${config.domain}.auth.${config.region}.amazoncognito.com`;
}

/** Post-login redirect target; the SPA completes the code exchange at /app/callback (§32.1). */
export function defaultRedirectUri(): string {
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:5174';
  return `${origin}/app/callback`;
}

function toBase64Url(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function randomBytes(length: number): Uint8Array {
  const out = new Uint8Array(length);
  globalThis.crypto.getRandomValues(out);
  return out;
}

/** base64url(SHA-256(input)). Exposed for testing the S256 challenge relationship. */
export async function s256(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await globalThis.crypto.subtle.digest('SHA-256', data);
  return toBase64Url(new Uint8Array(digest));
}

export interface PkcePair {
  readonly verifier: string;
  readonly challenge: string;
}

/**
 * Generate a PKCE `code_verifier` (43–128 char base64url) and its S256 `code_challenge`.
 * 32 random bytes → 43 base64url chars, within RFC 7636 bounds.
 */
export async function generatePkcePair(): Promise<PkcePair> {
  const verifier = toBase64Url(randomBytes(32));
  const challenge = await s256(verifier);
  return { verifier, challenge };
}

/** Opaque anti-forgery `state` value round-tripped through the hosted UI. */
export function randomState(): string {
  return toBase64Url(randomBytes(16));
}

export interface AuthorizeUrlParams {
  readonly challenge: string;
  readonly state: string;
  readonly redirectUri?: string;
  readonly config?: CognitoConfig;
}

/** Build the hosted-UI /oauth2/authorize URL for the Authorization Code + PKCE flow. */
export function buildAuthorizeUrl(params: AuthorizeUrlParams): string {
  const config = params.config ?? resolveCognitoConfig();
  const url = new URL(`${hostedUiOrigin(config)}/oauth2/authorize`);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('redirect_uri', params.redirectUri ?? defaultRedirectUri());
  url.searchParams.set('scope', config.scopes);
  url.searchParams.set('state', params.state);
  url.searchParams.set('code_challenge', params.challenge);
  url.searchParams.set('code_challenge_method', 'S256');
  return url.toString();
}

/** Hosted-UI logout URL that clears the Cognito session and returns to the app. */
export function buildLogoutUrl(
  logoutUri: string,
  config: CognitoConfig = resolveCognitoConfig(),
): string {
  const url = new URL(`${hostedUiOrigin(config)}/logout`);
  url.searchParams.set('client_id', config.clientId);
  url.searchParams.set('logout_uri', logoutUri);
  return url.toString();
}

export class TokenExchangeError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'TokenExchangeError';
    this.status = status;
  }
}

function toTokenSet(res: CognitoTokenResponse, fallbackRefresh: string | null): TokenSet {
  return {
    accessToken: res.access_token,
    idToken: res.id_token,
    refreshToken: res.refresh_token ?? fallbackRefresh,
    tokenType: res.token_type,
    expiresAt: Date.now() + res.expires_in * 1000,
  };
}

async function postToken(
  body: Record<string, string>,
  config: CognitoConfig,
): Promise<CognitoTokenResponse> {
  const res = await fetch(`${hostedUiOrigin(config)}/oauth2/token`, {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body).toString(),
  });
  if (!res.ok) {
    let detail = `token endpoint returned ${res.status}`;
    try {
      const json = (await res.json()) as { error?: string; error_description?: string };
      if (json.error) detail = json.error_description ?? json.error;
    } catch {
      /* non-JSON error body */
    }
    throw new TokenExchangeError(res.status, detail);
  }
  return (await res.json()) as CognitoTokenResponse;
}

/** Exchange an authorization `code` (with the stored verifier) for tokens. */
export async function exchangeCodeForTokens(
  code: string,
  verifier: string,
  redirectUri: string = defaultRedirectUri(),
  config: CognitoConfig = resolveCognitoConfig(),
): Promise<TokenSet> {
  const res = await postToken(
    {
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code,
      redirect_uri: redirectUri,
      code_verifier: verifier,
    },
    config,
  );
  return toTokenSet(res, null);
}

/** Silent refresh before expiry so a multi-hour gig is not interrupted (§32.3). */
export async function refreshTokens(
  refreshToken: string,
  config: CognitoConfig = resolveCognitoConfig(),
): Promise<TokenSet> {
  const res = await postToken(
    {
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    },
    config,
  );
  return toTokenSet(res, refreshToken);
}
